import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import {
  Container,
  ContentWrapper,
  EntryHeader,
  Footer,
  Header,
  Main,
  NavigationMenu,
  SEO,
} from '../../components';
import * as MENUS from '../../constants/menus';
import { BlogInfoFragment } from '../../fragments/GeneralSettings';
import { ArtikkelSEOFragment, OptimizedImageFragment } from '../../fragments/artikler/ArtikkelSEOFragment';
import { isBlogPost } from '../../constants/categories';
import React from 'react';
import Image from 'next/image';
import Head from 'next/head';
import styles from '../../styles/Single.module.scss';
import Link from 'next/link';
import { MediaBoosterSettingsFragment } from '../../fragments/MediaBoosterSettings';
import BlogPostSkeleton from '../../components/skeletonloader/BlogPostSkeleton';
import ContactBoxes from '../../components/ContactPage/ContactBoxes/ContactBoxes';
// Dynamic import for RelatedArticles to reduce initial bundle size
import dynamic from 'next/dynamic';

const RelatedArticles = dynamic(() => import('../../components/RelatedArticles'), {
  ssr: false,
  loading: () => <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading related articles...</div>
});

const normalizeSlug = (value = '') =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export default function BlogPost() {
  const router = useRouter();
  const { slug } = router.query;
  const { data, loading, error } = useQuery(BlogPost.query, {
    variables: BlogPost.variables({ slug }),
    skip: !slug,
    ssr: false,
    fetchPolicy: 'cache-first'
  });

  // Loading state
  if (loading || !slug) {
    return <BlogPostSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>Kunne ikke laste blogginnlegg</p>
        </div>
      </div>
    );
  }

  // Extract data from props
  const { posts, generalSettings, headerMenuItems, footerMenuItems, mediaboosterSettings } = data || {};
  const post = posts?.nodes?.[0];

  // If no post found, show 404
  if (!post) {
    router.push('/404');
    return null;
  }

  // If post does NOT have byword/bedriftsnettside tag, redirect to artikler
  if (!isBlogPost(post)) {
    router.replace(`/artikler/${slug}`);
    return <BlogPostSkeleton />;
  }

  // If essential data is missing, show a loading state
  if (!generalSettings) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>
          <p>Laster blogginnlegg...</p>
        </div>
      </div>
    );
  }

  const { title: siteTitle, description: siteDescription } = generalSettings;
  const primaryMenu = headerMenuItems?.nodes ?? [];
  const footerMenu = footerMenuItems?.nodes ?? [];
  const { title, content, featuredImage, date, author, categories, tags } = post;
  const lightLogo = mediaboosterSettings?.mediaboosterSettingsFields?.logo?.node;
  const footerLogo = mediaboosterSettings?.mediaboosterSettingsFields?.footerLogo?.node;
  const employees = data?.employees?.nodes || [];
  const tagSlugs = (tags?.nodes?.map((tag) => normalizeSlug(tag.slug || tag.name)).filter(Boolean) || []);
  const matchedEmployee = employees.find((employee) => {
    const employeeSlug = normalizeSlug(employee?.slug);
    const employeeUriSlug = normalizeSlug((employee?.uri || '').split('/').filter(Boolean).pop() || '');
    const employeeTitleSlug = normalizeSlug(employee?.title || '');

    return (
      (employeeSlug && tagSlugs.includes(employeeSlug)) ||
      (employeeUriSlug && tagSlugs.includes(employeeUriSlug)) ||
      (employeeTitleSlug && tagSlugs.includes(employeeTitleSlug))
    );
  }) || null;

  // Format the date
  const formattedDate = date ? new Date(date).toLocaleDateString('no-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  // Calculate reading time (rough estimate: 200 words per minute)
  const wordCount = content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0;
  const readingTime = Math.ceil(wordCount / 200);

  // Extract JSON-LD schema from Rank Math's fullHead
  // This includes Article schema + FAQ schema configured in WordPress
  const extractSchemaFromFullHead = (fullHead) => {
    if (!fullHead) return null;
    
    // Match all JSON-LD script tags
    const schemaRegex = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
    const matches = [];
    let match;
    
    while ((match = schemaRegex.exec(fullHead)) !== null) {
      try {
        matches.push(match[1].trim());
      } catch (e) {
        console.error('Error parsing schema from fullHead:', e);
      }
    }
    
    return matches.length > 0 ? matches : null;
  };

  const rankMathSchemas = extractSchemaFromFullHead(post?.seo?.fullHead);

  return (
    <>
      <Head>
        {/* Preload featured image for faster loading */}
        {featuredImage?.node?.mediaItemUrl && (
          <link 
            rel="preload" 
            href={featuredImage.node.mediaItemUrl} 
            as="image" 
            fetchPriority="high"
            imagesrcset={`${featuredImage.node.mediaItemUrl}?w=800&h=450&q=80 800w, ${featuredImage.node.mediaItemUrl}?w=400&h=225&q=80 400w`}
            imagesizes="(max-width: 768px) 100vw, 800px"
          />
        )}
        {/* Preconnect to WordPress domain for faster image loading */}
        <link rel="preconnect" href="https://mediaboosterhe.wpenginepowered.com" />
        <link rel="dns-prefetch" href="https://mediaboosterhe.wpenginepowered.com" />
        {/* Inject Rank Math schema (Article + FAQ) directly from WordPress */}
        {rankMathSchemas && rankMathSchemas.map((schema, index) => (
          <script
            key={`rankmath-schema-${index}`}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: schema }}
          />
        ))}
      </Head>
      <SEO seo={{
        ...post?.seo,
        canonicalUrl: `https://mediabooster.no/blogg/${slug}/`
      }} />
      <Header
        title={siteTitle}
        description={siteDescription}
        menuItems={primaryMenu}
        lightLogo={lightLogo}
      />
      <Main>
        <article className={styles.article}>
          <div className={styles.container}>
            <div className={styles.articleMeta}>
              <div className={styles.categoriesRow}>
                {categories?.nodes?.map((category, index) => (
                  <React.Fragment key={category.categoryId}>
                    <span className={styles.category}>
                      {category.name}
                    </span>
                    {index < categories.nodes.length - 1 && ' '}
                  </React.Fragment>
                ))}
              </div>
              <div className={styles.metaRow}>
                <span className={styles.date}>{formattedDate}</span>
                <span className={styles.readingTime}>{readingTime} minutter</span>
              </div>
            </div>

            <h1 className={styles.title}>{title}</h1>

            {featuredImage?.node && (
              <div className={styles.featuredImage}>
                <Image
                  src={featuredImage.node.sourceUrl || featuredImage.node.mediaItemUrl}
                  alt={featuredImage.node.altText || title}
                  width={featuredImage.node.mediaDetails?.width || 800}
                  height={featuredImage.node.mediaDetails?.height || 450}
                  priority
                  quality={85}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 800px"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover'
                  }}
                />
              </div>
            )}

            <div className={styles.contentWrapper}>
              <div className={styles.content} dangerouslySetInnerHTML={{ __html: content }} />

              <aside className={styles.sidebar}>
                <div className={styles.sidebarStack}>
                  <section className={styles.postContactSection}>
                    <ContactBoxes
                      employee={matchedEmployee}
                      email={matchedEmployee?.ansatte?.email ? matchedEmployee.ansatte.email.replace(/<[^>]*>/g, '') : 'post@mediabooster.no'}
                      showHeading={false}
                      compact
                    />
                  </section>

                  <section id="book-mote" className={styles.calendlySection} aria-label="Book et møte">
                    <h3 className={styles.calendlyHeading}>Snakk med en rådgiver</h3>
                    <div className={styles.calendlyEmbedWrapper}>
                      <iframe
                        src="https://calendly.com/sales-mediabooster/blogg-30-min?hide_event_type_details=1&hide_gdpr_banner=1"
                        title="Book møte med Mediabooster"
                        className={styles.calendlyIframe}
                      />
                    </div>
                  </section>
                </div>
              </aside>
            </div>
          </div>
        </article>
        
        <button
          className={styles.mobileBookBtn}
          onClick={() => document.getElementById('book-mote')?.scrollIntoView({ behavior: 'smooth' })}
          aria-label="Snakk med en rådgiver"
        >
          Snakk med en rådgiver
        </button>

        <RelatedArticles currentPost={post} />

      </Main>

      <Footer 
        title={siteTitle} 
        menuItems={footerMenu}
        settings={mediaboosterSettings}
      />
    </>
  );
}

BlogPost.query = gql`
  ${BlogInfoFragment}
  ${NavigationMenu.fragments.entry}
  ${MediaBoosterSettingsFragment}
  ${ArtikkelSEOFragment}
  ${OptimizedImageFragment}
  query GetBlogPost(
    $slug: String!,
    $headerLocation: MenuLocationEnum,
    $footerLocation: MenuLocationEnum
  ) {
    posts(where: { name: $slug, language: NB }) {
      nodes {
        ...ArtikkelSEOFragment
        databaseId
        title
        content
        date
        slug
        author {
          node {
            name
          }
        }
        categories {
          nodes {
            categoryId
            name
            slug
          }
        }
        tags {
          nodes {
            name
            slug
          }
        }
        featuredImage {
          node {
            ...OptimizedImageFragment
          }
        }
      }
    }
    employees: ansatte(first: 100, where: { language: NB }) {
      nodes {
        title
        slug
        uri
        ansattId
        ansatte {
          employeePhoto {
            node {
              ...OptimizedImageFragment
            }
          }
          role
          email
          linkedin
        }
      }
    }
    generalSettings {
      ...BlogInfoFragment
    }
    ...MediaBoosterSettingsFragment
    headerMenuItems: menuItems(first: 100, where: { location: $headerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
    footerMenuItems: menuItems(first: 100, where: { location: $footerLocation }) {
      nodes {
        ...NavigationMenuItemFragment
      }
    }
  }
`;

BlogPost.variables = ({ slug }) => {
  return {
    slug,
    headerLocation: MENUS.PRIMARY_LOCATION,
    footerLocation: MENUS.FOOTER_LOCATION,
  };
}; 